import {
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    EmbeddedViewRef,
    Injectable,
    Injector,
    Type
} from '@angular/core';
import {Subject, Subscription} from 'rxjs';

@Injectable()
export class DialogService {

    private componentRef: ComponentRef<DialogBase<unknown, unknown>>;
    private id: string;
    private cancelled: boolean;
    public emitDataSubject: Subject<DialogResult<any>>;

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector,
    ) {
    }

    public async createDialogComponent<TRequest, TResult>(
        component: Type<DialogBase<TRequest, TResult>>,
        data: TRequest,
        id: string
    ): Promise<DialogResult<TResult>> {
        this.emitDataSubject = new Subject<DialogResult<TResult>>();
        this.id = id;
        let dialog = document.getElementById(id) as DialogElement;

        this.componentRef = this.componentFactoryResolver
            .resolveComponentFactory(component)
            .create(this.injector);

        this.componentRef.instance.dialogInit(id, data);

        this.appRef.attachView(this.componentRef.hostView);

        if (!dialog) {
            const domElem = (this.componentRef.hostView as EmbeddedViewRef<DialogBase<TRequest, TResult>>)
                .rootNodes[0] as HTMLElement;

            document.body.appendChild(domElem);
            await new Promise(resolve => setTimeout(resolve, 10));

            const ctx = this;
            dialog = document.getElementById(id) as DialogElement;
            dialog.addEventListener(
                'cm-dialog-closed',
                () => {
                    ctx.removeComponent();
                },
                false
            );
        }

        this.cancelled = true;

        dialog.open();
        dialog.removeAttribute('isClosed');

        return new Promise<DialogResult<TResult>>((resolve, reject) => {
            let subscription: Subscription;
            subscription = this.emitDataSubject.subscribe(result => {
                subscription.unsubscribe();
                resolve(result as DialogResult<TResult>);
            });
        });
    }

    public removeComponent() {
        const dialog = document.getElementById(this.id) as DialogElement;

        if (!dialog?.getAttribute('isClosed')) {
            dialog.setAttribute('isClosed', 'true');
            dialog.close();
        } else {
            if (this.cancelled) {
                this.emitData(true, null);
            }
            this.appRef.detachView(this.componentRef.hostView);
            this.componentRef.destroy();
            document.body.removeChild(dialog);
        }
    }

    public set Cancelled(cancelled: boolean) {
        this.cancelled = cancelled;
    }

    public emitData(cancelled: boolean, data: any) {
        const result = {
            cancelled,
            data
        };

        this.emitDataSubject.next(result);
    }
}

interface DialogElement extends HTMLElement {
    close(): void;

    open(): void;
}

export interface DialogResult<T> {
    cancelled: boolean;
    data: T;
}

export abstract class DialogBase<TRequest, TResult> {
    private _id: string;

    constructor(protected dialogService: DialogService) {
    }

    public dialogInit(id: string, data: TRequest) {
        this._id = id;
        this.setModal(data);
    }

    protected abstract setModal(data: TRequest): void;

    public get id() {
        return this._id;
    }

    public cancel() {
        this.dialogService.Cancelled = true;
        this.dialogService.removeComponent();
        this.dialogService.emitData(true, null);
    }

    public confirm(data: TResult) {
        this.dialogService.Cancelled = false;
        this.dialogService.removeComponent();
        this.dialogService.emitData(false, data);
    }
}

export interface IInput {
    titleText?: { key: string, tags?: { key: string, value: string }[] };
    bodyText?: { key?: string, value?: string, tags?: { key: string, value: string }[] };
    cancelText?: string;
    confirmText?: string;
    extraData?: any;
}

export interface IOutput {
    successful: boolean;
    extraData: any;
}
